import { auth } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged,
} from "firebase/auth";

// ─── Generate a strong random password for auto-created accounts ─────────────
export function generatePassword() {
  const chars   = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const lower   = "abcdefghjkmnpqrstuvwxyz";
  const symbols = "@#$";
  // 8 chars: 2 upper, 2 lower, 2 digits, 2 symbols → easy to type but secure
  let pass = "";
  for (let i = 0; i < 3; i++) pass += chars[Math.floor(Math.random() * chars.length)];
  for (let i = 0; i < 3; i++) pass += lower[Math.floor(Math.random() * lower.length)];
  for (let i = 0; i < 1; i++) pass += symbols[Math.floor(Math.random() * symbols.length)];
  pass += Math.floor(Math.random() * 90 + 10); // 2 digit number
  return pass;
}

// ─── Sign up (called automatically at purchase) ──────────────────────────────
export async function signUp({ email, password }) {
  if (!auth) throw new Error("Firebase Auth not configured.");
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return result.user;
}

// ─── Sign in ─────────────────────────────────────────────────────────────────
export async function signIn({ email, password }) {
  if (!auth) throw new Error("Firebase Auth not configured.");
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (err) {
    // Translate Firebase error codes to friendly messages
    const codeMessages = {
      "auth/invalid-email":         "Invalid email format.",
      "auth/user-not-found":        "No account found with this email.",
      "auth/wrong-password":        "Incorrect password.",
      "auth/invalid-credential":    "Invalid email or password.",
      "auth/too-many-requests":     "Too many attempts. Try again in a few minutes.",
      "auth/network-request-failed": "Network error. Check your connection.",
    };
    throw new Error(codeMessages[err.code] || err.message);
  }
}

// ─── Sign out ────────────────────────────────────────────────────────────────
export async function signOutUser() {
  if (!auth) return;
  await signOut(auth);
}

// ─── Reset password (sends email) ────────────────────────────────────────────
export async function resetPassword(email) {
  if (!auth) throw new Error("Firebase Auth not configured.");
  await sendPasswordResetEmail(auth, email);
  return { success: true };
}

// ─── Change password (when logged in) ────────────────────────────────────────
export async function changePassword(newPassword) {
  if (!auth?.currentUser) throw new Error("Not logged in.");
  await updatePassword(auth.currentUser, newPassword);
  return { success: true };
}

// ─── Subscribe to auth state changes ─────────────────────────────────────────
export function watchAuthState(callback) {
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}
